import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LogOut, Download, Upload, Loader2, FileText, PenLine } from "lucide-react";
import { toast } from "sonner";
import MfaSettings from "@/components/admin/MfaSettings";
import IndicadoresBonificacion from "@/components/admin/bonificacion/IndicadoresBonificacion";
import {
  adminMe, bonificacionListFicheros, bonificacionDescargarFichero, bonificacionSubirRetorno, bonificacionDescargarInformePdf,
  haciendaListarCensos, haciendaFirmarCenso, haciendaDescargarCensoPdf,
} from "@/api";
import { getErrorMessage } from "@/lib/errors";

export default function TecnicoAyuntamientoDashboard() {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [ficheros, setFicheros] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [censos, setCensos] = useState([]);
  const [firmarId, setFirmarId] = useState(null);
  const [password, setPassword] = useState("");
  const [firmando, setFirmando] = useState(false);

  const loadCensos = () => haciendaListarCensos().then(setCensos).catch(() => {});

  useEffect(() => {
    const raw = localStorage.getItem("sfh_admin_session");
    if (!raw) { navigate("/admin"); return; }
    const parsed = JSON.parse(raw);
    if (parsed.rol !== "tecnico_ayuntamiento") { navigate("/admin/dashboard"); return; }
    setSession(parsed);
    adminMe().then((me) => setTotpEnabled(!!me.totp_enabled)).catch(() => {});
    bonificacionListFicheros().then(setFicheros).catch(() => {});
    loadCensos();
  }, [navigate]);

  const handleFirmar = async () => {
    setFirmando(true);
    try {
      await haciendaFirmarCenso(firmarId, password);
      toast.success("Censo firmado correctamente");
      setFirmarId(null);
      setPassword("");
      loadCensos();
    } catch (err) {
      toast.error(getErrorMessage(err, "No se pudo firmar el censo"));
    } finally {
      setFirmando(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("sfh_admin_token");
    localStorage.removeItem("sfh_admin_session");
    navigate("/admin");
  };

  const handleRetorno = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await bonificacionSubirRetorno(file);
      toast.success(`Fichero de retorno procesado: ${res.actualizados} filas`);
    } catch {
      toast.error("No se pudo procesar el fichero");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  if (!session) return null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="tecnico-ayuntamiento-dashboard">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center p-1 shrink-0">
              <img src="/logos/escudo-ayuntamiento.png" alt="Escudo Ayuntamiento" className="w-full h-full object-contain" />
            </div>
            <div>
              <p className="font-heading font-semibold text-municipal text-sm leading-tight">Ayuntamiento — Bonificación Tasa Basuras</p>
              <p className="text-xs text-gray-400 leading-tight">{session.nombre}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <MfaSettings enabled={totpEnabled} onChanged={() => setTotpEnabled((v) => !v)} />
            <Button variant="outline" size="sm" onClick={handleLogout} className="border-municipal text-municipal" data-testid="tecnico-logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="font-heading font-semibold text-municipal">Ficheros aprobados</h2>
            <label>
              <Button variant="outline" className="border-municipal text-municipal" disabled={uploading} asChild>
                <span>{uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />} Subir fichero de retorno</span>
              </Button>
              <input type="file" accept=".csv" className="hidden" onChange={handleRetorno} data-testid="tecnico-retorno-upload-input" />
            </label>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Periodo</TableHead>
                <TableHead>DNI / Cumplen</TableHead>
                <TableHead className="text-right">Descargar</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ficheros.length === 0 && (
                <TableRow><TableCell colSpan={3} className="text-center text-gray-400 py-8">Aún no hay ficheros aprobados disponibles.</TableCell></TableRow>
              )}
              {ficheros.map((f) => (
                <TableRow key={f.id} data-testid={`tecnico-fichero-row-${f.periodo}`}>
                  <TableCell className="font-medium text-municipal">{f.periodo} <Badge className="ml-2 bg-valoriza-green">aprobado</Badge></TableCell>
                  <TableCell>{f.total_dni} / {f.total_cumple}</TableCell>
                  <TableCell className="text-right space-x-1">
                    {["A", "B", "C"].map((tipo) => (
                      <Button key={tipo} size="sm" variant="ghost" onClick={() => bonificacionDescargarFichero(f.id, tipo, f.periodo)} data-testid={`tecnico-descargar-${tipo}-${f.periodo}`}>
                        <Download className="w-3 h-3 mr-1" />{tipo}
                      </Button>
                    ))}
                    <Button size="sm" variant="ghost" className="text-municipal" onClick={() => bonificacionDescargarInformePdf(f.id, f.periodo)} data-testid={`tecnico-descargar-pdf-${f.periodo}`}>
                      <FileText className="w-3 h-3 mr-1" />PDF
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <IndicadoresBonificacion />

        <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="tecnico-censo-section">
          <h2 className="font-heading font-semibold text-municipal mb-4">Censo de Medio Ambiente — pendiente de firma</h2>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Servicio</TableHead>
                <TableHead>Año</TableHead>
                <TableHead>Registros</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {censos.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-gray-400 py-8">No hay censos generados todavía.</TableCell></TableRow>
              )}
              {censos.map((c) => (
                <TableRow key={c.id} data-testid={`tecnico-censo-row-${c.id}`}>
                  <TableCell className="capitalize">{c.servicio === "puntolimpio" ? "Punto Limpio Móvil" : c.servicio}</TableCell>
                  <TableCell>{c.anio}</TableCell>
                  <TableCell>{c.total_registros}</TableCell>
                  <TableCell>
                    <Badge className={c.estado === "firmado" ? "bg-valoriza-green text-white" : "bg-amber-100 text-amber-700"}>
                      {c.estado === "firmado" ? "Firmado" : "Pendiente de firma"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="ghost" onClick={() => haciendaDescargarCensoPdf(c.id, c.servicio, c.anio)} data-testid={`tecnico-censo-descargar-${c.id}`}>
                      <Download className="w-3 h-3 mr-1" />PDF
                    </Button>
                    {c.estado !== "firmado" && (
                      <Button size="sm" variant="ghost" className="text-municipal" onClick={() => setFirmarId(c.id)} data-testid={`tecnico-censo-firmar-${c.id}`}>
                        <PenLine className="w-3 h-3 mr-1" />Firmar
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </main>

      <Dialog open={!!firmarId} onOpenChange={(v) => !v && setFirmarId(null)}>
        <DialogContent className="max-w-sm" data-testid="tecnico-censo-firmar-dialog">
          <DialogHeader>
            <DialogTitle className="text-municipal font-heading">Firma electrónica simple</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500 mb-2">
            Confirma tu contraseña para firmar este censo como técnico/a de Medio Ambiente.
          </p>
          <Label className="text-xs mb-1 block">Contraseña</Label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="tecnico-censo-password-input" />
          <Button onClick={handleFirmar} disabled={!password || firmando} className="w-full bg-municipal hover:bg-municipal-dark text-white mt-3" data-testid="tecnico-censo-confirmar-btn">
            {firmando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Firmar censo
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
